import { MRT_ColumnDef } from 'mantine-react-table'
import { Text, Tooltip, Avatar, Anchor } from '@mantine/core'
import { ReactNode } from 'react'
import TradeClosingActions from './trade-closing-actions'
import getInitials from 'app/views/components/functions/getInitials'
import StatusPill from '../deals-management/common/pill-status'
import { formatDateToShow, formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'
import { AssignmentListItem, AssignmentParty } from 'app/models/assignment-list-params'

function TableHead({ header }: { header: string }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {header}
    </Text>
  )
}

function TableRowText({ text }: { text: ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {text}
    </Text>
  )
}

/** One avatar per trade's party, its initials from the name the read resolved. */
function PartyAvatars({ parties, keyPrefix }: { parties: AssignmentParty[]; keyPrefix: string }) {
  return (
    <Tooltip.Group openDelay={300} closeDelay={100}>
      <Avatar.Group spacing="sm">
        {parties?.map((party, index) => (
          <Anchor key={`${keyPrefix}-${party.id}-${index}`}>
            <Tooltip label={party.name ?? ''} withArrow>
              <Avatar size="lg" color="teal" radius="xl">
                {getInitials(party.name ?? '')}
              </Avatar>
            </Tooltip>
          </Anchor>
        ))}
      </Avatar.Group>
    </Tooltip.Group>
  )
}

/**
 * What the CSV carries, in the grid's column order. `MainTable`'s own export
 * reads `row[accessorKey]` and would write `[object Object]` for the two
 * party columns, which are lists, so the page hands it this instead
 * (`customExportData`) — the names a reader expects, as the Trade List does.
 * The amount stays a plain number (the grid formats it as currency; a
 * spreadsheet sums the number), with the currency as its own column.
 */
export const assignmentsForExport = (rows: AssignmentListItem[]) =>
  rows.map((row) => ({
    'Deal Name': row.dealName ?? '',
    'Facility Name': row.facilityName ?? '',
    'Effective Date': row.effectiveDate ? formatDateToShow(stringToDate(row.effectiveDate)) : '',
    Seller: partyNames(row.sellers),
    Buyer: partyNames(row.buyers),
    // a plain number, as the page exported it: a spreadsheet sums it as is
    Amount: row.amount,
    Currency: row.baseCurrency ?? '',
    Status: row.status,
    id: row.id,
  }))

/**
 * The parties in one cell: "Seller One, Buyer Two". The grid draws a party
 * once per trade; a cell names it once — a repeat says nothing in a CSV.
 * Once per party, by id: two parties may share a name.
 */
const partyNames = (parties: AssignmentParty[]) =>
  [...new Map((parties ?? []).map((p) => [p.id, p])).values()].map((p) => p.name ?? p.id).join(', ')

/**
 * The Trade Closing grid's columns. Every value is on the row already
 * (ADR_LOS-030): `GET /api/assignment-list` resolved the deal and facility
 * names, both sides' entity names, the currency and the summed amount, so
 * this page loads no deal, facility or entity list to render a row.
 */
export default function getColumnDefs(): MRT_ColumnDef<AssignmentListItem>[] {
  return [
    {
      size: 40,
      // `id` is the key the grid's saved filters, sorting and column
      // visibility are stored under, so it outlives the field behind it.
      // `accessorKey` is the field: MainTable's CSV export reads
      // `row[accessorKey]`, so dropping it would export a blank column.
      id: 'dealId',
      accessorKey: 'dealName',
      header: 'Deal name',
      Header: <TableHead header={'Deal Name'} />,
      Cell: ({ row }) => <TableRowText text={row.original.dealName ?? ''} />,
    },
    {
      size: 40,
      id: 'facilityId',
      accessorKey: 'facilityName',
      header: 'Facility Name',
      Header: <TableHead header={'Facility Name'} />,
      Cell: ({ row }) => <TableRowText text={row.original.facilityName ?? ''} />,
    },
    {
      size: 50,
      accessorKey: 'effectiveDate',
      header: 'Effective Date',
      Header: <TableHead header={'Effective Date'} />,
      accessorFn: row => stringToDate(row.effectiveDate ?? ''),
      mantineFilterTextInputProps: {
        placeholder: 'Date',
      },
      filterFn: 'lessThanOrEqualTo',
      filterVariant: 'date',
      sortingFn: 'datetime',
      Cell: ({ cell }) => (
        <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
      ), //render Date as a string
    },
    {
      size: 50,
      id: 'from',
      accessorKey: 'sellers',
      header: 'Seller',
      Header: <TableHead header={'Seller'} />,
      Cell: ({ row }) => <PartyAvatars parties={row.original.sellers} keyPrefix="seller" />,
    },
    {
      size: 50,
      id: 'to',
      accessorKey: 'buyers',
      header: 'Buyer',
      Header: <TableHead header={'Buyer'} />,
      Cell: ({ row }) => <PartyAvatars parties={row.original.buyers} keyPrefix="buyer" />,
    },
    {
      size: 50,
      accessorKey: 'amount',
      header: 'Amount',
      Header: <TableHead header={'Amount'} />,
      Cell: ({ row }) => (
        <TableRowText
          text={formatNumberToCurrency(
            Number(row.original.amount),
            row.original.baseCurrency ?? 'USD',
          )}
        />
      ),
    },
    {
      size: 40,
      accessorKey: 'status',
      header: 'Status',
      Header: <TableHead header={'Status'} />,
      Cell: ({ cell }: any) => {
        return <StatusPill state={cell.getValue()} />
      },
    },
    {
      size: 40,
      accessorKey: 'id',
      header: 'Actions',
      Header: <TableHead header={'Actions'} />,
      enableColumnActions: false,
      enableColumnFilter: false,
      Cell: cell => <TradeClosingActions row={cell.row.original} />,
    },
  ]
}
